import { useState, useEffect, useCallback } from 'react';
import { Loader, Plus, Pencil, Trash2, Copy, Check, Tag, Link, Search, Unlink, AlertCircle } from 'lucide-react';
import {
  PartnershipCreatorCode,
  CreatorCodeStatus,
  CreateCreatorCodeRequest,
  UpdateCreatorCodeRequest,
} from '../../types/partnership';
import { partnershipsApiService } from '../../services/partnershipsApi';
import { useDebounce } from '../../hooks/useDebounce';
import { cn } from '../../utils/cn';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface PartnershipCodesSectionProps {
  partnershipId: string;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  onUpdate: () => void;
}

const STATUS_COLORS: Record<CreatorCodeStatus, string> = {
  active: 'badge-success',
  expired: 'badge-warning',
  revoked: 'badge-danger',
};

export function PartnershipCodesSection({
  partnershipId,
  canCreate,
  canEdit,
  canDelete,
  onUpdate,
}: PartnershipCodesSectionProps) {
  const [codes, setCodes] = useState<PartnershipCreatorCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showLinkExisting, setShowLinkExisting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [copiedCodeId, setCopiedCodeId] = useState<string | null>(null);

  // Create form state
  const [createCode, setCreateCode] = useState('');
  const [createDiscount, setCreateDiscount] = useState(0);
  const [createRevenueShare, setCreateRevenueShare] = useState(0);
  const [createNotes, setCreateNotes] = useState('');

  // Link existing code state
  const [linkSearch, setLinkSearch] = useState('');
  const [allCodes, setAllCodes] = useState<PartnershipCreatorCode[]>([]);
  const [allCodesLoading, setAllCodesLoading] = useState(false);
  const [linking, setLinking] = useState<string | null>(null);
  const debouncedLinkSearch = useDebounce(linkSearch, 300);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getCodesForPartnership(partnershipId);
      setCodes(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  }, [partnershipId]);

  useEffect(() => {
    loadCodes();
  }, [loadCodes]);

  useEffect(() => {
    if (!showLinkExisting) return;
    setAllCodesLoading(true);
    partnershipsApiService.getAllCodes()
      .then(setAllCodes)
      .catch(console.error)
      .finally(() => setAllCodesLoading(false));
  }, [showLinkExisting]);

  const handleLinkCode = async (codeId: string) => {
    setLinking(codeId);
    try {
      await partnershipsApiService.linkCodeToPartnership(codeId, partnershipId);
      await loadCodes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to link code');
    } finally {
      setLinking(null);
    }
  };

  const handleUnlinkCode = async (codeId: string) => {
    if (!window.confirm('Unlink this code from this partnership? The code will still exist.')) return;
    setDeletingId(codeId);
    try {
      await partnershipsApiService.unlinkCodeFromPartnership(codeId, partnershipId);
      await loadCodes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unlink code');
    } finally {
      setDeletingId(null);
    }
  };

  const handleCopyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCodeId(id);
    setTimeout(() => setCopiedCodeId(null), 2000);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreate || submitting) return;
    setSubmitting(true);
    try {
      const data: CreateCreatorCodeRequest = {
        code: createCode.trim(),
        discount_percent: createDiscount,
        revenue_share_percent: createRevenueShare,
        notes: createNotes.trim() || undefined,
      };
      await partnershipsApiService.createCreatorCode(partnershipId, data);
      setCreateCode('');
      setCreateDiscount(0);
      setCreateRevenueShare(0);
      setCreateNotes('');
      setShowCreate(false);
      await loadCodes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (codeId: string, data: UpdateCreatorCodeRequest) => {
    if (!canEdit || submitting) return;
    setSubmitting(true);
    try {
      await partnershipsApiService.updateCode(codeId, data);
      setEditingId(null);
      await loadCodes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update code');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (codeId: string) => {
    if (!canDelete || deletingId) return;
    const confirmed = window.confirm('Are you sure you want to delete this code?');
    if (!confirmed) return;
    setDeletingId(codeId);
    try {
      await partnershipsApiService.deleteCode(codeId);
      await loadCodes();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete code');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-28" />
          <SkeletonLoader className="h-9 w-32 rounded-lg" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="subcard p-3 space-y-2">
            <div className="flex items-center gap-2">
              <SkeletonLoader className="h-4 w-28" />
              <SkeletonLoader className="h-5 w-14 rounded-full" />
            </div>
            <SkeletonLoader className="h-3 w-48" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load codes"
        description={error}
        action={
          <button type="button" onClick={loadCodes} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Creator Codes"
        icon={Tag}
        count={codes.length}
        actions={
          canCreate ? (
            <>
              <button
                type="button"
                onClick={() => { setShowLinkExisting(!showLinkExisting); setShowCreate(false); }}
                className="glass-button-secondary"
                aria-expanded={showLinkExisting}
              >
                <Link className="w-4 h-4" />
                Link Existing
              </button>
              <button
                type="button"
                onClick={() => { setShowCreate(!showCreate); setShowLinkExisting(false); }}
                className="glass-button"
                aria-expanded={showCreate}
              >
                <Plus className="w-4 h-4" />
                New Code
              </button>
            </>
          ) : undefined
        }
      />

      {/* Link existing code panel */}
      {showLinkExisting && canCreate && (
        <div className="subcard p-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400" />
            <input
              type="text"
              value={linkSearch}
              onChange={(e) => setLinkSearch(e.target.value)}
              className="glass-input w-full pl-9"
              placeholder="Search existing codes by name…"
              autoFocus
            />
          </div>
          {allCodesLoading ? (
            <div className="space-y-2 py-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <SkeletonLoader key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-0.5 max-h-[250px] overflow-y-auto">
              {(() => {
                const linkedIds = new Set(codes.map(c => c.id));
                const filtered = allCodes.filter(c => {
                  if (linkedIds.has(c.id)) return false;
                  if (!debouncedLinkSearch) return true;
                  const q = debouncedLinkSearch.toLowerCase();
                  return c.code.toLowerCase().includes(q) || (c.notes?.toLowerCase().includes(q));
                });
                if (filtered.length === 0) {
                  return <p className="text-sm text-glass-500 py-2 px-1">{debouncedLinkSearch ? 'No matching codes found' : 'All codes are already linked'}</p>;
                }
                return filtered.map(code => (
                  <div key={code.id} className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-md hover:bg-glass-200/10 transition-colors">
                    <div className="flex items-center gap-2 min-w-0 flex-wrap">
                      <code className="font-mono text-sm text-white font-medium">{code.code}</code>
                      <span className={cn('badge badge-dot', STATUS_COLORS[code.status])}>{code.status}</span>
                      <span className="text-xs text-glass-500 tabular-nums">{code.discount_percent}% off · {code.revenue_share_percent}% rev</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLinkCode(code.id)}
                      disabled={linking === code.id}
                      className="glass-button glass-button-sm flex-shrink-0"
                    >
                      {linking === code.id ? <Loader className="w-3 h-3 animate-spin" /> : <Link className="w-3 h-3" />}
                      Link
                    </button>
                  </div>
                ));
              })()}
            </div>
          )}
          <button type="button" onClick={() => setShowLinkExisting(false)} className="glass-button-secondary w-full">
            Close
          </button>
        </div>
      )}

      {showCreate && canCreate && (
        <form onSubmit={handleCreate} className="subcard p-4 space-y-3">
          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5">Code</label>
            <input
              type="text"
              value={createCode}
              onChange={(e) => setCreateCode(e.target.value)}
              placeholder="e.g. CREATOR10"
              className="glass-input w-full font-mono"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Discount %</label>
              <input
                type="number"
                min={0}
                max={100}
                value={createDiscount}
                onChange={(e) => setCreateDiscount(Number(e.target.value) || 0)}
                className="glass-input w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">Revenue Share %</label>
              <input
                type="number"
                min={0}
                max={100}
                value={createRevenueShare}
                onChange={(e) => setCreateRevenueShare(Number(e.target.value) || 0)}
                className="glass-input w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
            <textarea
              value={createNotes}
              onChange={(e) => setCreateNotes(e.target.value)}
              placeholder="Optional notes"
              rows={2}
              className="glass-input w-full resize-none"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Create
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {codes.length === 0 ? (
          <EmptyState
            bare
            compact
            icon={Tag}
            title="No codes yet"
            description="Create a code for this partnership, or link an existing code to keep its usage and history together."
          />
        ) : (
          codes.map((code) => (
            <div
              key={code.id}
              className="subcard p-3 space-y-2"
            >
              {editingId === code.id && canEdit ? (
                <CodeEditForm
                  code={code}
                  onSave={(data) => handleUpdate(code.id, data)}
                  onCancel={() => setEditingId(null)}
                  submitting={submitting}
                />
              ) : (
                <>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-medium text-white">{code.code}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyCode(code.code, code.id)}
                        className="btn-icon btn-icon-sm"
                        title="Copy code"
                        aria-label="Copy code"
                      >
                        {copiedCodeId === code.id ? (
                          <Check className="w-3.5 h-3.5 text-green-400" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                      </button>
                      <span className={cn('badge badge-dot', STATUS_COLORS[code.status])}>
                        {code.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => setEditingId(code.id)}
                          className="btn-icon btn-icon-sm"
                          title="Edit"
                          aria-label="Edit code"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      )}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => handleUnlinkCode(code.id)}
                          disabled={deletingId === code.id}
                          className={cn(
                            'btn-icon btn-icon-sm text-amber-400 hover:text-amber-300',
                            deletingId === code.id && 'opacity-50 cursor-not-allowed'
                          )}
                          title="Unlink from this partnership (code still exists)"
                          aria-label="Unlink code from this partnership"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(code.id)}
                          disabled={deletingId === code.id}
                          className={cn(
                            'btn-icon btn-icon-sm text-red-400 hover:text-red-300',
                            deletingId === code.id && 'opacity-50 cursor-not-allowed'
                          )}
                          title="Delete permanently"
                          aria-label="Delete code permanently"
                        >
                          {deletingId === code.id ? (
                            <Loader className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-glass-400 tabular-nums">
                    <span>Discount: <span className="text-glass-200">{code.discount_percent}%</span></span>
                    <span>Revenue Share: <span className="text-glass-200">{code.revenue_share_percent}%</span></span>
                  </div>
                  {code.notes && (
                    <p className="text-sm text-glass-400">{code.notes}</p>
                  )}
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function CodeEditForm({
  code,
  onSave,
  onCancel,
  submitting,
}: {
  code: PartnershipCreatorCode;
  onSave: (data: UpdateCreatorCodeRequest) => void;
  onCancel: () => void;
  submitting: boolean;
}) {
  const [codeVal, setCodeVal] = useState(code.code);
  const [discount, setDiscount] = useState(code.discount_percent);
  const [revenueShare, setRevenueShare] = useState(code.revenue_share_percent);
  const [notes, setNotes] = useState(code.notes || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      code: codeVal.trim(),
      discount_percent: discount,
      revenue_share_percent: revenueShare,
      notes: notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Code</label>
        <input
          type="text"
          value={codeVal}
          onChange={(e) => setCodeVal(e.target.value)}
          className="glass-input w-full font-mono"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Discount %</label>
          <input
            type="number"
            min={0}
            max={100}
            value={discount}
            onChange={(e) => setDiscount(Number(e.target.value) || 0)}
            className="glass-input w-full"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-glass-400 mb-1.5">Revenue Share %</label>
          <input
            type="number"
            min={0}
            max={100}
            value={revenueShare}
            onChange={(e) => setRevenueShare(Number(e.target.value) || 0)}
            className="glass-input w-full"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-glass-400 mb-1.5">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="glass-input w-full resize-none"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="glass-button-secondary">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary min-w-[5rem]">
          {submitting ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  );
}
